import { ReactNode, useMemo, useState } from "react";
import t from "@/lang/lang";
import { useCustomersCustomerListQuery } from "@/services/backend/customers/api/customer-api";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { FilterMode } from "@/shared/lib/filter/filter";
import { TableCard } from "@/shared/components/tables/table-card";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { Input } from "@/shared/components/ui/input";
import { LimitSelect } from "@/shared/components/tables/pagination-limit-offset";
import { useDebounce } from "@/shared/lib/debounce/debounce";
import { Button } from "@/shared/components/ui/button";
import { CreateCustomerDialog } from "@/services/backend/customers/components/create-customer-dialog";
import { oneLine } from "../../addresses/types/address";

interface Props {
  renderRowLink: (customerId: string, children: ReactNode) => ReactNode;
  showCreateCustomerButton?: boolean;
}

export function CustomersTable({
  renderRowLink,
  showCreateCustomerButton = false,
}: Props) {
  const [search, setSearch] = useState("");
  const name = useDebounce(search, 200);
  const [limit, setLimit] = useState(20);

  const request = useMemo(
    () => ({
      name: { active: true, mode: FilterMode.Like, values: [name] },
      limit,
    }),
    [name, limit],
  );

  const { data: list, error } = useCustomersCustomerListQuery(request, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
  });

  return (
    <div className="space-y-2">
      <div className="flex flex-col gap-2 lg:flex-row lg:justify-between">
        <div className="flex flex-col gap-2 md:flex-row md:items-center">
          <Input
            className="w-full md:max-w-md"
            placeholder={t("Name suchen")}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
            }}
          />
          {showCreateCustomerButton && (
            <div className="self-start md:self-auto">
              <CreateCustomerDialog />
            </div>
          )}
        </div>
        <LimitSelect
          limit={request.limit!}
          setLimitOffset={(l) => setLimit(l)}
        />
      </div>
      <TableCard>
        <Table>
          <TableHeader>
            <TableRow>
              <TableCell>{t("Kundenname")}</TableCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {list &&
              list.items.map((customer) => (
                <TableRow key={customer.id}>
                  <TableCell className="py-0">
                    <Button variant="link" size="sm" asChild>
                      {renderRowLink(customer.id, <>{customer.name}</>)}
                    </Button>
                    <span className="text-muted-foreground">
                      {oneLine(customer.address)}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
        <RTKQueryErrorAlert error={error} className="rounded-t-none" />
      </TableCard>
    </div>
  );
}
